import React, { useEffect, useMemo, useState } from "react";
import {
  Modal, Table, Button, Input,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { updateTestResult } from "../../../actions/sendReports";

const editableFieldKeys = Object.freeze({
  WORK_ORDER: "WO#",
  RESULT: "Result",
  GENISTA_ID: "GenistaID",
  TEST_NAME: "test_name",
});

const getWorkOrderValue = (row) => (row == null ? null : row[editableFieldKeys.WORK_ORDER]);
const getNormalizedText = (value) => (value === null || value === undefined ? "" : String(value).trim());
const getSortableText = (value) => getNormalizedText(value).toLowerCase();

const confirmationFieldMap = {
  result: editableFieldKeys.RESULT,
  genistaid: editableFieldKeys.GENISTA_ID,
  test_name: editableFieldKeys.TEST_NAME,
  "wo#": editableFieldKeys.WORK_ORDER,
};

const normalizeConfirmationFieldName = (fieldName) => {
  const normalizedFieldName = String(fieldName).trim();
  if (!normalizedFieldName) {
    return null;
  }
  return confirmationFieldMap[normalizedFieldName.toLowerCase()] || normalizedFieldName;
};

const getConfirmationFieldNames = (row) => {
  if (!Array.isArray(row?.is_confirmation_needed)) {
    return [];
  }
  return row.is_confirmation_needed.map(normalizeConfirmationFieldName).filter(Boolean);
};

const shouldRequireConfirmation = (row) => getConfirmationFieldNames(row).length > 0;

const getRowStatusTag = (row) => {
  if (shouldRequireConfirmation(row)) {
    return "need_confirmation";
  }
  const normalizedMessage = getNormalizedText(row?.Message).toLowerCase();
  if (normalizedMessage.includes("success") || normalizedMessage.includes("successfully updated the result")) {
    return "success";
  }
  if (normalizedMessage) {
    return "failed";
  }
  const statusKey = String(row?.Status).trim().toLowerCase();
  if (statusKey === "confirmation_needed") return "need_confirmation";
  if (statusKey === "fail") return "failed";
  if (statusKey === "pass") return "success";
  return undefined;
};

const getStatusPriority = (statusTag) => {
  if (statusTag === "need_confirmation") return 0;
  if (statusTag === "failed") return 1;
  if (statusTag === "success") return 2;
  return 3;
};

const compareAscendingText = (firstValue, secondValue) => (
  getSortableText(firstValue).localeCompare(getSortableText(secondValue), undefined, {
    numeric: true,
    sensitivity: "base",
  })
);

const compareRowsForDisplayOrder = (firstRow, secondRow) => {
  const statusComparison = getStatusPriority(firstRow.statusTag) - getStatusPriority(secondRow.statusTag);
  if (statusComparison !== 0) return statusComparison;

  const workOrderComparison = compareAscendingText(
    getWorkOrderValue(firstRow),
    getWorkOrderValue(secondRow),
  );
  if (workOrderComparison !== 0) return workOrderComparison;

  const genistaIdComparison = compareAscendingText(firstRow?.GenistaID, secondRow?.GenistaID);
  if (genistaIdComparison !== 0) return genistaIdComparison;

  return compareAscendingText(firstRow.normalizedResult, secondRow.normalizedResult);
};

const buildTableRows = (resultRows, testName) => {
  const rows = Array.isArray(resultRows) ? resultRows : [];
  return rows.map((row, index) => ({
    ...row,
    key: `${row.GenistaID || "row"}-${index}`,
    originalIndex: index,
    testName,
    statusTag: getRowStatusTag(row),
    normalizedResult: getNormalizedText(row?.result || row?.Result),
  }));
};

const hasRowKey = (rows, fieldName) => rows.some(
  (row) => Object.prototype.hasOwnProperty.call(row, fieldName),
);

const ChemUploadResultsModal = ({
  isOpen, testName, resultRows, isMlUpload, onConfirm,
}) => {
  const [editableValues, setEditableValues] = useState({});
  const [savedNeedConfirmationRowKeys, setSavedNeedConfirmationRowKeys] = useState([]);
  const [attemptedNeedConfirmationRowKeys, setAttemptedNeedConfirmationRowKeys] = useState([]);
  const [savingRowKeys, setSavingRowKeys] = useState([]);
  const [editingNeedConfirmationRowKeys, setEditingNeedConfirmationRowKeys] = useState([]);
  const [isForceCloseConfirmOpen, setIsForceCloseConfirmOpen] = useState(false);

  const tableDataSource = useMemo(() => {
    const tableRows = buildTableRows(resultRows, testName);
    return [...tableRows].sort((firstRow, secondRow) => {
      const rowOrder = compareRowsForDisplayOrder(firstRow, secondRow);
      if (rowOrder !== 0) {
        return rowOrder;
      }
      return firstRow.originalIndex - secondRow.originalIndex;
    });
  }, [resultRows, testName]);

  const getEditableFieldNamesForRow = (row) => {
    if (!isMlUpload) {
      return [];
    }
    const editableFieldNames = [...getConfirmationFieldNames(row)];
    const isWorkOrderMissing = getNormalizedText(getWorkOrderValue(row)).length === 0;
    if (isWorkOrderMissing && !editableFieldNames.includes(editableFieldKeys.WORK_ORDER)) {
      editableFieldNames.push(editableFieldKeys.WORK_ORDER);
    }
    return editableFieldNames;
  };

  const isRowEditable = (row) => getEditableFieldNamesForRow(row).length > 0;
  const isFieldEditableForRow = (row, fieldName) => getEditableFieldNamesForRow(row).includes(fieldName);
  const getEditableFieldValue = (rowKey, fieldName) => editableValues[rowKey]?.[fieldName] ?? "";

  useEffect(() => {
    const nextEditableValues = {};
    const needConfirmationRows = [];
    tableDataSource.forEach((row) => {
      if (!isRowEditable(row)) {
        return;
      }
      const confirmationFields = getEditableFieldNamesForRow(row);
      const nextRowEditableValues = {};
      confirmationFields.forEach((fieldName) => {
        if (fieldName === editableFieldKeys.RESULT) {
          nextRowEditableValues[editableFieldKeys.RESULT] = row.normalizedResult;
          return;
        }
        nextRowEditableValues[fieldName] = getNormalizedText(row?.[fieldName]);
      });
      nextEditableValues[row.key] = nextRowEditableValues;
      needConfirmationRows.push(row.key);
    });
    setEditableValues(nextEditableValues);
    setSavedNeedConfirmationRowKeys([]);
    setAttemptedNeedConfirmationRowKeys([]);
    setSavingRowKeys([]);
    setEditingNeedConfirmationRowKeys(needConfirmationRows);
    setIsForceCloseConfirmOpen(false);
  }, [tableDataSource, isOpen, isMlUpload]); // eslint-disable-line react-hooks/exhaustive-deps

  const needConfirmationRows = tableDataSource.filter(isRowEditable);
  const hasRowsNeedingConfirmation = needConfirmationRows.length > 0;
  const shouldShowGenistaIdColumn = hasRowKey(tableDataSource, editableFieldKeys.GENISTA_ID);
  const shouldShowTestNameColumn = hasRowKey(tableDataSource, editableFieldKeys.TEST_NAME);
  const shouldShowResultColumn = hasRowKey(tableDataSource, "result")
    || hasRowKey(tableDataSource, editableFieldKeys.RESULT);
  const shouldShowMessageColumn = hasRowKey(tableDataSource, "Message");
  const isConfirmDisabled = needConfirmationRows.some(
    (row) => !attemptedNeedConfirmationRowKeys.includes(row.key),
  );
  const shouldShowForceCloseWarning = needConfirmationRows.every(
    (row) => attemptedNeedConfirmationRowKeys.includes(row.key),
  ) && needConfirmationRows.some(
    (row) => attemptedNeedConfirmationRowKeys.includes(row.key) && !savedNeedConfirmationRowKeys.includes(row.key),
  );

  const handleChangeNeedConfirmationValue = (rowKey, fieldName, value) => {
    setEditableValues((previousValues) => ({
      ...previousValues,
      [rowKey]: { ...(previousValues[rowKey] || {}), [fieldName]: value },
    }));
  };

  const handleStartNeedConfirmationEdit = (rowKey) => {
    setEditingNeedConfirmationRowKeys((previousKeys) => (
      previousKeys.includes(rowKey) ? previousKeys : [...previousKeys, rowKey]
    ));
  };

  const markRowSaveAttempted = (rowKey) => {
    setAttemptedNeedConfirmationRowKeys((previousKeys) => (
      previousKeys.includes(rowKey) ? previousKeys : [...previousKeys, rowKey]
    ));
  };

  const handleSaveNeedConfirmationRow = async (row) => {
    const confirmationFields = getEditableFieldNamesForRow(row);
    const normalizedEditableValues = confirmationFields.reduce((accumulator, fieldName) => ({
      ...accumulator,
      [fieldName]: getNormalizedText(getEditableFieldValue(row.key, fieldName)),
    }), {});

    const updatedWorkOrderId = normalizedEditableValues[editableFieldKeys.WORK_ORDER] || getWorkOrderValue(row);
    const updatedResult = normalizedEditableValues[editableFieldKeys.RESULT] ?? row.normalizedResult;
    const updatedGenistaId = normalizedEditableValues[editableFieldKeys.GENISTA_ID] || row.GenistaID;
    const updatedTestName = normalizedEditableValues[editableFieldKeys.TEST_NAME] || row?.test_name || testName;

    const hasEmptyConfirmationField = confirmationFields.some(
      (fieldName) => getNormalizedText(normalizedEditableValues[fieldName]).length === 0,
    );
    if (hasEmptyConfirmationField) {
      toast.warn("Please fill all required confirmation fields before saving.");
      return;
    }
    if (!updatedWorkOrderId) {
      toast.error("Unable to save: missing work order.");
      return;
    }
    if (!updatedGenistaId) {
      toast.error("Unable to save: missing GenistaID.");
      return;
    }

    const payload = {
      GenistaID: updatedGenistaId,
      Analysis: updatedTestName,
      Result: updatedResult,
      OrderID: updatedWorkOrderId,
      source: "Automation - Chemistry - Upload",
    };

    setSavingRowKeys((previousKeys) => [...previousKeys, row.key]);
    try {
      const response = await updateTestResult(payload);
      markRowSaveAttempted(row.key);
      if (!response?.success) {
        toast.error(response?.message || "Failed to save result.");
        return;
      }

      setSavedNeedConfirmationRowKeys((previousKeys) => (
        previousKeys.includes(row.key) ? previousKeys : [...previousKeys, row.key]
      ));
      setEditingNeedConfirmationRowKeys((previousKeys) => previousKeys.filter((item) => item !== row.key));
      setEditableValues((previousValues) => ({
        ...previousValues,
        [row.key]: {
          ...(previousValues[row.key] || {}),
          [editableFieldKeys.WORK_ORDER]: updatedWorkOrderId,
          [editableFieldKeys.GENISTA_ID]: updatedGenistaId,
          [editableFieldKeys.TEST_NAME]: updatedTestName,
          [editableFieldKeys.RESULT]: updatedResult,
        },
      }));
      toast.success(response?.message || "Result saved.");
    } catch (error) {
      markRowSaveAttempted(row.key);
      toast.error("Failed to save result.");
    } finally {
      setSavingRowKeys((previousKeys) => previousKeys.filter((item) => item !== row.key));
    }
  };

  const handleCloseRequest = () => {
    if (shouldShowForceCloseWarning) {
      setIsForceCloseConfirmOpen(true);
      return;
    }
    onConfirm({ wasForceClose: false });
  };

  const handleForceCloseConfirm = () => {
    setIsForceCloseConfirmOpen(false);
    onConfirm({ wasForceClose: true });
  };

  const columns = [
    {
      title: "WO#",
      dataIndex: editableFieldKeys.WORK_ORDER,
      key: editableFieldKeys.WORK_ORDER,
      width: "12%",
      className: "ChemMlUploadResultsModal__mono",
      onHeaderCell: () => ({ className: "ChemMlUploadResultsModal__mono" }),
      render: (_, record) => {
        if (isFieldEditableForRow(record, editableFieldKeys.WORK_ORDER)) {
          const isSaving = savingRowKeys.includes(record.key);
          const isEditing = editingNeedConfirmationRowKeys.includes(record.key);
          return isEditing ? (
            <Input
              className="ChemMlUploadResultsModal__editableInput"
              size="middle"
              value={getEditableFieldValue(record.key, editableFieldKeys.WORK_ORDER)}
              onChange={(event) => handleChangeNeedConfirmationValue(record.key, editableFieldKeys.WORK_ORDER, event.target.value)}
              disabled={isSaving}
              placeholder="Enter WO#"
            />
          ) : getEditableFieldValue(record.key, editableFieldKeys.WORK_ORDER);
        }
        return getWorkOrderValue(record) || "";
      },
    },
    ...(shouldShowGenistaIdColumn ? [{
      title: "GenistaID",
      dataIndex: "GenistaID",
      key: "GenistaID",
      width: "16%",
      className: "ChemMlUploadResultsModal__mono",
      onHeaderCell: () => ({ className: "ChemMlUploadResultsModal__mono" }),
      render: (_, record) => {
        if (!isFieldEditableForRow(record, editableFieldKeys.GENISTA_ID)) {
          return record?.[editableFieldKeys.GENISTA_ID] || "";
        }
        const isSaving = savingRowKeys.includes(record.key);
        const isEditing = editingNeedConfirmationRowKeys.includes(record.key);
        const content = isEditing ? (
          <Input
            className="ChemMlUploadResultsModal__editableInput"
            size="middle"
            value={getEditableFieldValue(record.key, editableFieldKeys.GENISTA_ID)}
            onChange={(event) => handleChangeNeedConfirmationValue(record.key, editableFieldKeys.GENISTA_ID, event.target.value)}
            disabled={isSaving}
            placeholder="Enter confirmed GenistaID"
          />
        ) : getEditableFieldValue(record.key, editableFieldKeys.GENISTA_ID);
        return content;
      },
    }] : []),
    ...(shouldShowTestNameColumn ? [{
      title: "Test name",
      dataIndex: "test_name",
      key: "testName",
      width: "20%",
      render: (_, record) => {
        if (!isFieldEditableForRow(record, editableFieldKeys.TEST_NAME)) {
          return record?.test_name || "";
        }
        const isSaving = savingRowKeys.includes(record.key);
        const isEditing = editingNeedConfirmationRowKeys.includes(record.key);
        if (!isEditing) return getEditableFieldValue(record.key, editableFieldKeys.TEST_NAME);
        return (
          <Input
            className="ChemMlUploadResultsModal__editableInput"
            size="middle"
            value={getEditableFieldValue(record.key, editableFieldKeys.TEST_NAME)}
            onChange={(event) => handleChangeNeedConfirmationValue(record.key, editableFieldKeys.TEST_NAME, event.target.value)}
            disabled={isSaving}
            placeholder="Enter confirmed test name"
          />
        );
      },
    }] : []),
    ...(shouldShowResultColumn ? [{
      title: "Result",
      dataIndex: "result",
      key: "result",
      width: "22%",
      render: (_, record) => {
        if (isFieldEditableForRow(record, editableFieldKeys.RESULT)) {
          const isSaving = savingRowKeys.includes(record.key);
          const isEditing = editingNeedConfirmationRowKeys.includes(record.key);
          if (!isEditing) return getEditableFieldValue(record.key, editableFieldKeys.RESULT);
          return (
            <Input
              className="ChemMlUploadResultsModal__editableInput ChemMlUploadResultsModal__resultInput"
              size="middle"
              value={getEditableFieldValue(record.key, editableFieldKeys.RESULT)}
              onChange={(event) => handleChangeNeedConfirmationValue(record.key, editableFieldKeys.RESULT, event.target.value)}
              disabled={isSaving}
              placeholder="Enter confirmed result"
            />
          );
        }
        return record.normalizedResult === "" ? "" : record.normalizedResult;
      },
    }] : []),
    ...(shouldShowMessageColumn ? [{
      title: "Message",
      dataIndex: "Message",
      key: "Message",
      width: "20%",
      render: (value) => (value ? <span className="ChemMlUploadResultsModal__statusText">{String(value)}</span> : ""),
    }] : []),
    ...(hasRowsNeedingConfirmation ? [{
      title: "Action",
      key: "Action",
      width: "10%",
      align: "center",
      className: "ChemMlUploadResultsModal__actionCol",
      onHeaderCell: () => ({ className: "ChemMlUploadResultsModal__actionCol" }),
      render: (_, record) => {
        if (!isRowEditable(record)) return "";
        const isSaving = savingRowKeys.includes(record.key);
        const isEditing = editingNeedConfirmationRowKeys.includes(record.key);
        if (isEditing) {
          return (
            <Button
              type="primary"
              size="middle"
              className="ChemMlUploadResultsModal__saveResultBtn"
              onClick={() => handleSaveNeedConfirmationRow(record)}
              loading={isSaving}
              disabled={isSaving}
            >
              Save
            </Button>
          );
        }
        return (
          <Button
            type="text"
            className="ChemMlUploadResultsModal__editActionBtn"
            icon={<EditOutlined className="ChemMlUploadResultsModal__editIcon" />}
            onClick={() => handleStartNeedConfirmationEdit(record.key)}
            disabled={isSaving}
            aria-label="Edit confirmation values"
          >
            Edit
          </Button>
        );
      },
    }] : []),
  ];

  return (
    <Modal
      className="ChemMlUploadResultsModal"
      open={isOpen}
      title={(
        <div className="ChemMlUploadResultsModal__titleBlock">
          <div className="ChemMlUploadResultsModal__titleMain">Upload Results Review</div>
          {hasRowsNeedingConfirmation && (
            <div className="ChemMlUploadResultsModal__subtitle">
              Review, edit required confirmation fields, save each required row, then Close.
            </div>
          )}
        </div>
      )}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={(
        <div className="ChemMlUploadResultsModal__footerActions">
          {shouldShowForceCloseWarning && (
            <span className="ChemMlUploadResultsModal__forceCloseWarning">
              Some values are still unsaved. Closing the review will discard unsaved changes and results will not be saved.
            </span>
          )}
          <Button
            type="primary"
            className="ChemMlUploadResultsModal__footerCloseBtn"
            onClick={handleCloseRequest}
            aria-label="Close upload results review"
            disabled={isConfirmDisabled}
          >
            Close
          </Button>
        </div>
      )}
      width="min(1100px, 94vw)"
      destroyOnClose
      centered
    >
      <Table
        size="small"
        pagination={false}
        columns={columns}
        dataSource={tableDataSource}
        sticky
        scroll={{ y: "calc(70vh - 100px)" }}
      />
      <Modal
        open={isForceCloseConfirmOpen}
        title="Force close review?"
        onCancel={() => setIsForceCloseConfirmOpen(false)}
        onOk={handleForceCloseConfirm}
        okText="Force Close"
        cancelText="Go Back"
        centered
      >
        Some values are not saved. If you continue, this will force close the review and discard unsaved changes.
      </Modal>
    </Modal>
  );
};

export default ChemUploadResultsModal;
